/**
 * Use promotional grid to surface and feature more than one promotional card at a time without using a rail
 *
 * @module ULTA_SDK
 * @tutorial SDK
 */
import React, { useEffect, useReducer, useRef, useState } from 'react';
import ReactDOM from 'react-dom';

import { gql } from '@apollo/client';
import _get from 'lodash/get';

import AsyncComponent from '@ulta/core/components/AsyncComponent/AsyncComponent';
import { onResponseDataCapture } from '@ulta/core/hooks/useDXLQuery/useDXLQuery';
import { getRequiredClientParams } from '@ulta/core/hooks/useLayerHostAction/useLayerHostAction';
import { useSubscription } from '@ulta/core/hooks/useSubscription/useSubscription';
import { useAppConfigContext } from '@ulta/core/providers/AppConfigProvider/AppConfigProvider';
import { useDeviceInflection } from '@ulta/core/providers/InflectionProvider/InflectionProvider';
import { useLayerHostContext } from '@ulta/core/providers/LayerHostProvider/LayerHostProvider';
import { useOverlay } from '@ulta/core/providers/OverlayProvider/OverlayProvider';
import { usePageDataContext } from '@ulta/core/providers/PageDataProvider/PageDataProvider';
import { useUserContext } from '@ulta/core/providers/UserContextProvider/UserContextProvider';
import { getClient } from '@ulta/core/utils/apollo_client/apollo_client';
import { handleDXLNavigationType } from '@ulta/core/utils/clientActionProcessor/clientActionProcessor';
import { DXL_NAVIGATION_TYPE } from '@ulta/core/utils/constants/action';
import datacapture from '@ulta/core/utils/datacapture/datacapture';
import { isServer } from '@ulta/core/utils/device_detection/device_detection';
import { devLogger, LOG_TOPIC } from '@ulta/core/utils/devMode/devMode';
import { spreadProps } from '@ulta/core/utils/dom/dom';
import { handleEmptyObjects } from '@ulta/core/utils/handleEmptyObjects/handleEmptyObjects';
import { queryProcessor } from '@ulta/core/utils/queryProcessor/queryProcessor';
import { getStorage, setStorage } from '@ulta/core/utils/storage/storage';
import { STORAGE_KEY } from '@ulta/core/utils/storageKeys/storageKeys';

import * as utils from './Sdk';

/**
 * @type {Array<object>}
 * @summary Initial empty state for React Portals.
 * @description This constant defines the initial empty array for the portals state,
 * which is used to manage dynamically rendered components throughout the application.
 */
export const initialPortals = [];

/**
 * @type {object}
 * @summary Initial empty state for subscriptions.
 * @description This constant provides the initial empty object for the subscriptions state,
 * which holds all the active subscriptions managed by the SDK.
 */
export const initialSubscriptions = {};

/**
 * @summary Main SDK component that orchestrates the entire ULTA_SDK functionality.
 * @description This component initializes the global ULTA_SDK object, manages its state,
 * handles events from iframes, and serves as the root for rendering dynamic UI elements
 * via React Portals. It integrates with various application-level contexts to configure
 * and expose a cohesive SDK API.
 * @param {object} props The properties for the SDK component.
 * @param {object} [props.initAction] The initial action to be executed by the SDK.
 * @param {string[]} [props.allowedIframeOrigins=[]] A list of allowed origins for iframe communication.
 * @param {object} props.contentModel The main content model from DXL.
 * @param {object} [props.shoppingAgentModel] The content model for the shopping agent.
 * @param {object} props.dataCapture The base configuration for data capture events.
 * @param {object} props.session The session data from DXL.
 * @param {object} props.ui The UI configuration from DXL.
 * @returns {React.Element} A React fragment containing portals for dynamic components.
 */
export const SDK = ( props ) => {
  const { isStaging, isNative, graphqlURI, graphqlDomain, graphqlServerUri } = useAppConfigContext();
  const { initAction, allowedIframeOrigins = [], contentModel, shoppingAgentModel, dataCapture: data_capture, session, ui, updateGPCAction } = props;

  const flyoutContentRef = useRef();
  const { user } = useUserContext();
  const [subscriptionEvents, setSubscriptionEvents] = useState( utils.flattenObjRecursively( user ) );
  const { openOverlay, closeOverlay } = useOverlay();
  const { refetchRef, DONOTUSE_broadcastInitAction } = usePageDataContext();
  const { breakpoint: { CURRENT_BREAKPOINT } = {} } = useDeviceInflection();
  const { broadcast } = useLayerHostContext();

  const [_contentModel, setContentModel] = useState();
  const [_shoppingAgentModel, setShoppingAgentModel] = useState();
  const [portals, dispatchPortalUpdate] = useReducer( utils.portalsReducer, utils.initialPortals );
  const [subscriptions, dispatchSubscriptionUpdate] = useReducer( utils.subscriptionReducer, utils.initialSubscriptions );

  const triggerDCEvent = utils.triggerDataCaptureEvent( { dataCapture: data_capture, allowedIframeOrigins } );

  useEffect( () => {
    // if the user has changed from the user context provider we need to reset the subscriptioEvents for outside consumption
    setSubscriptionEvents( utils.flattenObjRecursively( user ) );
  }, [user] );

  useSubscription( { val: user, subscriptions }, { dispatchSubscriptionUpdate } );
  useEffect( () => {
    // add the sdk event listener for handling message posts
    if( !isServer() ){
      global.addEventListener( 'message', utils.handleIframeEvent );
    }

    return () => global.removeEventListener( 'message', utils.handleIframeEvent );
  }, [] );

  useEffect( () => {
    if( !isServer() ){
      let newCm;

      if( !contentModel ){
        let updatedContentModel = contentModel || {};
        updatedContentModel.ConversationalAI = {
          moduleName: 'ConversationalAI'
        };
        setContentModel( updatedContentModel );
      }
      else if( contentModel && !contentModel.ConversationalAI ){
        contentModel.ConversationalAI = {
          moduleName: 'ConversationalAI'
        };
        setContentModel( contentModel );
      }
      newCm = utils.distributeRenderMethods( { contentModel }, { dispatchPortalUpdate } );
      newCm = utils.configureGetMethods(
        { contentModel: newCm, CURRENT_BREAKPOINT, user, isStaging, isNative, graphqlURI, graphqlDomain, graphqlServerUri },
        { setContentModel, DONOTUSE_broadcastInitAction }
      );
      newCm = utils.configureModuleInvoke(
        { contentModel: newCm, CURRENT_BREAKPOINT, refetchRef, user, isStaging, isNative, graphqlURI, graphqlDomain, graphqlServerUri },
        { invoke: utils.invokeModuleAction, DONOTUSE_broadcastInitAction }
      );
      const init = utils.initSDK( { action: initAction } );
      const enrichedUI = utils.configureUIActions(
        { ui, flyoutContentRef },
        { openOverlay, closeOverlay, refetchRef, dispatchPortalUpdate, broadcast }
      );
      const enrichedSession = utils.configureSessionData(
        { contentModel: newCm, session, user, subscriptions, subscriptionEvents, refetchRef, isStaging, isNative, graphqlURI, graphqlDomain, graphqlServerUri },
        { dispatchSubscriptionUpdate, openOverlay }
      );
      const dataCapture = {
        trigger: triggerDCEvent
      };

      global.ULTA_SDK = {
        ...( global.ULTA_SDK || {} ),
        init,
        contentModel,
        session: enrichedSession,
        ui: enrichedUI,
        dataCapture,
        updateGPCAction
      };
    }

  }, [contentModel] ); // first time we receive cntent model from DXL

  useEffect( () => {
    utils.configureGetMethods(
      { contentModel: _contentModel, CURRENT_BREAKPOINT, user, isStaging, isNative, graphqlURI, graphqlDomain, graphqlServerUri },
      { setContentModel, DONOTUSE_broadcastInitAction }
    );
    utils.configureModuleInvoke(
      { contentModel: _contentModel, CURRENT_BREAKPOINT, refetchRef, user, isStaging, isNative, graphqlURI, graphqlDomain, graphqlServerUri },
      { invoke: utils.invokeModuleAction, DONOTUSE_broadcastInitAction }
    );
    const enrichedSession = utils.configureSessionData(
      { contentModel: _contentModel, session, user, subscriptions, subscriptionEvents, refetchRef, isStaging, isNative, graphqlURI, graphqlDomain, graphqlServerUri },
      { dispatchSubscriptionUpdate, openOverlay }
    );

    global.ULTA_SDK = {
      ...global.ULTA_SDK,
      contentModel: _contentModel,
      session: enrichedSession
    };
  }, [_contentModel, CURRENT_BREAKPOINT, user] );

  const configureShoppingAgentModel = ( model )=>{
    /**
     * This is a stop gap solution to remove the keys with null or undefined values.
     * The follow up ticket has been crated to clean it up here ->  https://ulta.atlassian.net/browse/MFBOLT-2428
     * */
    let newShoppingAgentModel = {};
    Object.keys( model ).forEach( ( key ) => {
      if( model[key] ){
        newShoppingAgentModel[key] = model[key];
      }
    } );
    newShoppingAgentModel = utils.distributeRenderMethods( { contentModel: newShoppingAgentModel }, { dispatchPortalUpdate } );
    newShoppingAgentModel = utils.configureGetMethods(
      { contentModel: newShoppingAgentModel, CURRENT_BREAKPOINT, user, isStaging, isNative, graphqlURI, graphqlDomain, graphqlServerUri },
      { setContentModel: setShoppingAgentModel, DONOTUSE_broadcastInitAction }
    );
    newShoppingAgentModel = utils.configureModuleInvoke(
      { contentModel: newShoppingAgentModel, CURRENT_BREAKPOINT, refetchRef, user, isStaging, isNative, graphqlURI, graphqlDomain, graphqlServerUri },
      { invoke: utils.invokeModuleAction, setContentModel: setShoppingAgentModel, DONOTUSE_broadcastInitAction }
    );
    newShoppingAgentModel = utils.configurePostMethods(
      { contentModel: newShoppingAgentModel, CURRENT_BREAKPOINT, user, isStaging, isNative, graphqlURI, graphqlDomain, graphqlServerUri },
      { setContentModel: setShoppingAgentModel, DONOTUSE_broadcastInitAction }
    );
    global.ULTA_SDK = {
      ... global.ULTA_SDK,
      shoppingAgentModel: newShoppingAgentModel
    };
  };

  useEffect( ()=>{
    if( shoppingAgentModel ){
      configureShoppingAgentModel( shoppingAgentModel );
    }
  }, [
    shoppingAgentModel
  ] );


  return (
    <>
      { user?.hasResolvedSessionData && shoppingAgentModel?.shoppingAgent && <AsyncComponent { ...spreadProps( { ...shoppingAgentModel.shoppingAgent } ) } moduleName={ 'ShoppingAgent' }/> }
      { portals.length > 0 &&
      portals?.map( ( portal, index ) => {
        const { moduleName, data, selector } = portal;
        const targetElement = document.querySelector( selector );

        if( !targetElement ){
          return null;
        }

        return ReactDOM.createPortal(
          <AsyncComponent
            { ...spreadProps( { ...data } ) }
            key={ `${moduleName}_${index}` }
            moduleName={ moduleName }
          />,
          targetElement
        );
      } ) }
    </>
  );
};

/**
 * initSDK
 * @summary a manual invocationmethod of the SDK API from
 * @param { object } data - data to be used in curried method
 * @memberof SDK
 * @returns function
 */
export const initSDK = ( data ) => {
  const { initAction } = data || {};

  return ( callback ) => {};
};

/**
 * handleIframeEvent
 * event handler method for frame events for trigger datacapture methods sent from within a fram
 * @method
 *
 * @param { object } e - event object
 *  @returns function
 */
export const handleIframeEvent = ( data, methods ) => {
  const { allowedIframeOrigins = [] } = data || {};
  const { triggerDCEvent, logger = devLogger } = methods || {};

  return ( e ) => {
    const matchedOrigin = allowedIframeOrigins?.filter( ( allowedOrigin ) => {
      return e.origin === allowedOrigin;
    } );
    if( matchedOrigin.length > 0 ){
      const eventData = e.detail?.data || {};
      logger( {
        title: 'iFrame eventCaptured',
        value: {
          eventData: eventData
        }
      } );
      triggerDCEvent( { ...eventData, iframeSrc: true } );
    }
  };
};

/**
 * flattenObjRecursively takes an object and returns a flattened formatted object only containing the keys that had data
 * @method
 * @param { object } obj - Object to be flattened
 */
export const flattenObjRecursively = ( obj = {} ) => {
  return Object.keys( obj ).reduce( ( acc, entry ) => {
    let newAcc = { ...acc };
    let subObj = obj[entry];
    if( typeof subObj === 'object' && subObj !== null ){
      newAcc = { ...newAcc, ...utils.flattenObjRecursively( obj[entry] ) };
    }
    else {
      newAcc[entry.toUpperCase()] = entry.toLowerCase();
    }
    return newAcc;
  }, {} );
};

/**
 * configureSessionData defines the public exposed session object for SDK usage
 * @method
 * @param { object } data - data objects
 * @param { object } methods - injected methods
 * @returns object
 */
export const configureSessionData = ( data, methods ) => {
  const { session, user, subscriptions, subscriptionEvents, contentModel, refetchRef, isStaging, isNative, graphqlURI, graphqlDomain, graphqlServerUri } = data || {};

  const { dispatchSubscriptionUpdate, openOverlay } = methods;

  const newSession = {
    user,
    // expose the login action to invoke action from dxl session obj
    login: utils.triggerQuery(
      { action: session?.signInAction, contentModel, refetchRef, actionPath: '_LOGIN', isStaging, isNative, graphqlURI, graphqlDomain, graphqlServerUri },
      { openOverlay }
    ),
    subscriptionEvents,
    subscribe: utils.subscribeToSessionChanges( { user }, { dispatchSubscriptionUpdate } ),
    getSubscriptions: utils.getSubscriptions( subscriptions ),
    unsubscribe: utils.unsusbscribeToSessionChanges( { dispatchSubscriptionUpdate } )
  };

  return newSession;
};

/**
 * getSubscriptions retuns the subscriptions object for consumption
 * @method
 * @param { object } subscriptions - object to be returned
 */
export const getSubscriptions = ( subscriptions ) => subscriptions;

/**
 * unsubscribeToSessionChanges
 * @method
 * @param { object } methods - object containing value of the dispatch method
 * @returns function
 */
export const unsusbscribeToSessionChanges = ( methods ) => {
  const { dispatchSubscriptionUpdate } = methods || {};
  if( !dispatchSubscriptionUpdate ){
    return;
  }

  return ( key ) => {
    if( !key ){
      return;
    }
    dispatchSubscriptionUpdate( { type: key, unsubscribe: true } );
  };
};

/**
 * subscribeToSessionChange allows the caller to add a callback function to be executed
 * to a specific session data key
 * @method
 *
 * @param { object }
 *
 * @returns function
 */
export const subscribeToSessionChanges = ( data, methods ) => {
  const { user } = data || {};
  const { dispatchSubscriptionUpdate } = methods || {};
  if( !dispatchSubscriptionUpdate ){
    return;
  }
  return ( key, callback ) => {
    if( !key || !callback ){
      return;
    }
    dispatchSubscriptionUpdate( { type: key, callback, lastVal: user[key] } );
  };
};

/**
 * subsctipionReducer
 *
 * managed the state of new incoming subscriptions passed by the callers
 *
 * @method
 * @param { object } state - current state object
 * @param { object } action - action being passed to manipulate teh state
 *
 * @returns object
 *
 */
export const subscriptionReducer = ( state = {}, action ) => {
  const { type, callback, lastVal, unsubscribe = false } = action || {};

  if( unsubscribe ){
    let newState = { ...state };
    delete newState[type];
    return newState;
  }
  else {
    // validate subscription
    //
    return {
      ...state,
      ...( type && callback && { [type]: { callback, lastVal } } )
    };
  }
};

/**
 * protalsReducer is used to manage react portals to display new UI elemtns within the application context
 * @method
 *
 *
 * @param { object } state - current state object
 * @param { object } action - action being passed to manipulate teh state
 *
 * @returns object
 */
export const portalsReducer = ( state = utils.initialPortals, action ) => {
  const { type, data, selector, moduleName, domain, portalID, unrenderType } = action || {};

  switch ( type ){
    case utils.RENDER_TYPE.RENDER:
      if( !type || !data || !selector || !moduleName || !domain || !portalID ){
        return state;
      }
      const entry = {
        id: portalID,
        moduleName,
        selector,
        data
      };
      return [...state, entry];

    case utils.RENDER_TYPE.UNRENDER:
      // TODO needs t ochange to a filter method for delete

      if( unrenderType === UNRENDER_TYPES.SINGLE ){
        if( !portalID ){
          console.error('to unrender a single module you MUST pass a selector and the contentmodel domain'); // eslint-disable-line
          return state;
        }
        const filteredRenderedModules = state.filter( ( module ) => module.id !== portalID );
        return filteredRenderedModules;
      }
      else if( unrenderType === UNRENDER_TYPES.ALL ){
        const filteredRenderedModules = state.filter( ( module ) => module.moduleName !== moduleName );
        return filteredRenderedModules;
      }
    default:
      console.error(`could not perform the ${type} for the ${domain} domain`); // eslint-disable-line
      return state;
  }
};

/**
 * RENDER_TYPE
 * define the types of render that  can be passed
 *
 * @param RENDER_TYPE
 * @param RENDER_TYPE.RENDER value for the type of render
 * @param RENDER_TYPE.UNRENDER value for the type of unrender
 */
export const RENDER_TYPE = {
  RENDER: 'render',
  UNRENDER: 'unrender'
};

/**
 * triggerDataCaptureEvent
 * @method
 *
 *
 */
export const triggerDataCaptureEvent = ( data ) => {
  const { dataCapture } = data || {};
  return ( innerdata ) => {
    const { type, name, data, useBaseConig = true, iframeSrc } = innerdata || {};
    if( type && data && !isServer() ){
      // TODO spread the base datacapture object with the new one to get desired output here
      const dcEvent = {
        ...( useBaseConig && { dataCapture } ),
        eventName: name,
        moduleName: 'ULTA_SDK',
        category: type,
        dataLayer: {
          ...dataCapture.dataLayer,
          Tealium: {
            ...( dataCapture.dataLayer?.Tealium || {} ),
            ...data
          }
        }
      };


      datacapture.processDataCaptureEvent( dcEvent );
    }
    else {
      return false;
    }
  };
};

/**
 * refreshPage - method that tell the application to refresh
 * @method
 *
 * @param data - data object to be passed
 * @param data.refetchRef - the react reference object forcalling refresh page
 * @param methods - methods passed as a configuration object
 * @param methods.refreshPageAction -  action for triggering a refetch page
 */
export const refreshPage = ( data, methods ) => {
  const { refetchRef } = data || {};
  const { refreshPageAction } = methods || {};
  return () => {
    handleDXLNavigationType( { action: refreshPageAction }, { refetch: refetchRef.current } );
  };
};

/**
 * handleOpenOverlay
 * event handler for opening an overlay
 * @method
 *
 * @param  outerdata - data for curried method instantiation
 * @param methods - methods for cirreid method instantiation
 *
 * @returns method
 */
export const handleOpenOverlay = ( outerdata, methods ) => {
  const { ui, flyoutContentRef } = outerdata || {};
  const { openOverlay, closeOverlay } = methods || {};
  return ( type, data ) => {
    // closeOverlay();
    const { alignment = 'right', crossButtonVisibility = true, loadDXLContentAction, content, onClose } = data || {};

    let overlayAction;
    switch ( type.toLowerCase() ){
      case DXL_NAVIGATION_TYPE.Flyout:
        overlayAction = ui.openFlyoutAction;
        break;
      case DXL_NAVIGATION_TYPE.Modal:
        overlayAction = ui.openModalAction;
        break;
      default:
        overlayAction = ui.openFlyoutAction;
        break;
    }

    const { navigationType, ...rest } = overlayAction || {};

    openOverlay( {
      type: navigationType,
      ...( type === DXL_NAVIGATION_TYPE.Flyout && { alignment } ),
      crossButtonVisibility,
      ...( loadDXLContentAction && { action: loadDXLContentAction } ),
      ...( content && {
        content: (
          <div
            id={ `SDK_Overlay_${type}` }
            ref={ flyoutContentRef }
          >
          </div>
        )
      } ),
      ...( onClose && { onClose } )
    } );
  };
};

/**
 * configureModuleInvoke - setups up the invoke action to be publicly exposed on the ULTA_SDK object
 * @method
 * @param data - data object containg data to be passed
 * @param methods - object contianing injected methods to be executed
 * @returns object
 */

export const configureModuleInvoke = ( data, methods ) => {
  const { contentModel = {}, user, refetchRef, CURRENT_BREAKPOINT, isStaging, isNative, graphqlURI, graphqlDomain, graphqlServerUri } = data || {};
  const { invoke, setContentModel, DONOTUSE_broadcastInitAction } = methods || {};
  let enrichedContentModel = { ...contentModel };
  Object.keys( contentModel )?.map( ( key ) => {
    const targetModule = enrichedContentModel[key];
    targetModule.invokeAction = invoke( {
      contentModel,
      targetModule,
      moduleName: targetModule.moduleName,
      domain: key,
      refetchRef,
      CURRENT_BREAKPOINT,
      user,
      isStaging,
      isNative,
      graphqlURI,
      graphqlDomain,
      graphqlServerUri
    }, { setContentModel, DONOTUSE_broadcastInitAction } );
  } );
  return enrichedContentModel;
};

export const broadcastSDKMeta = ( data, methods ) => {
  const { meta = {} } = handleEmptyObjects( data );
  const { DONOTUSE_broadcastInitAction } = methods || {};

  // This function populates user.shoppingAgentCart value in localstorage so handleGetCart can immediately use the value.
  // DONOTUSE_broadcastInitAction will wait for state rendering to complete, by the time handleGetCart executed,
  // user.shoppingAgentCart value in localStorage wont be populated
  utils.populateStorageWithCartData( { meta } );

  const broadcastMeta = Object.fromEntries(
    Object.entries( meta ).filter( ( [_, value] ) => value !== undefined )
  );

  if( !DONOTUSE_broadcastInitAction || Object.keys( broadcastMeta ).length === 0 ){
    return;
  }

  devLogger( {
    topic: LOG_TOPIC.Sdk,
    title: '[SDK] Broadcasting meta',
    value: {
      source: 'sdk',
      sideEffectsCount: broadcastMeta.sideEffects?.length || 0,
      sideEffectTypes: broadcastMeta.sideEffects?.map( sideEffect => Object.keys( sideEffect || {} ) ).flat() || [],
      keys: Object.keys( broadcastMeta )
    }
  } );

  DONOTUSE_broadcastInitAction( {
    initAction: broadcastMeta.initAction,
    snackBar: broadcastMeta.snackBar,
    protectedFlow: !!broadcastMeta.initAction?.graphql,
    meta: broadcastMeta
  } );
};

/**
 * populateStorageWithCartData
 * populates the shoppingAgentCart in storage with the data from meta
 * @method
 *
 * @param { object } data - data objects
 *
 * @returns void
 */
export const populateStorageWithCartData = function( { meta } ){
  const sessionData = meta?.sessionData;
  if( sessionData && Object.prototype.hasOwnProperty.call( sessionData, 'shoppingAgentCart' ) ){
    const shoppingAgentCart = sessionData.shoppingAgentCart;
    const userFromStorage = getStorage( { key: STORAGE_KEY.user, secure: false } ) || {};
    setStorage( {
      key: STORAGE_KEY.user,
      value: { ...userFromStorage, shoppingAgentCart },
      secure: false
    } );
  }
};

/**
 * triggerQuery returns a function for the SDK user to invoke for excuting an action
 * @method
 *
 * @param data - data object containg data to be passed
 * @param methods - object contianing injected methods to be executed
 *
 * @returns function
 */
export const triggerQuery = function( data, methods ){
  const { action, contentModel, actionPath, refetchRef, CURRENT_BREAKPOINT, user, domain, isStaging, isNative, graphqlURI, graphqlDomain, graphqlServerUri } = data || {};
  const { openOverlay, setContentModel, DONOTUSE_broadcastInitAction } = methods || {};

  return async function( customModuleParams = {} ){
    if( !action ){
      console.warn('there was an issue trying to invoke the action'); // eslint-disable-line
      return null;
    }

    // retrieve the CM content items data
    const module = contentModel?.[domain]?._data?.getAction || {};
    let clientParamsResolvedAction = getRequiredClientParams( {
      user,
      props: module,
      action,
      breakpoint: CURRENT_BREAKPOINT
    } );

    const preppedAction = queryProcessor( { action: clientParamsResolvedAction } );

    devLogger( {
      topic: LOG_TOPIC.Sdk,
      title: `[SDK] invokeAction for ${domain}`,
      value: { user, preppedAction }
    } );

    const { graphql, config, context, isMutation, variables, method } = preppedAction || {};
    const dxlClient = getClient();

    // get the action type
    // TODO   determine if we need to run a DXL navigation or a client navion using the data from the action here

    const wasActionHandled = handleDXLNavigationType( { action }, { refetch: refetchRef.current, openOverlay } );

    if( graphql && !wasActionHandled ){
      const actionType = isMutation ? 'mutate' : 'query';
      const { data: dxlData } = await dxlClient[actionType]( {
        [isMutation ? 'mutation' : 'query']: gql`
          ${graphql}
        `,
        ...config,
        context: { ...context, fetchOptions: { method } },
        variables: {
          contentId: module.id, // add the module Id for the inquery from the module itself
          ...variables,
          moduleParams: {
            ...module,
            ...variables.moduleParams, // add all of the moduleParams. This is needed because not all actions define params for themselves
            ...customModuleParams
          }
        }
      } );

      if( dxlData?.Page?.meta?.dataCaptureEventList ){
        onResponseDataCapture( dxlData.Page.meta.dataCaptureEventList );
      }

      utils.broadcastSDKMeta( { meta: dxlData?.Page?.meta }, { DONOTUSE_broadcastInitAction } );

      const res = dxlData?.Page?.content;
      if( res ){
        // if it's a page query then we will
        let updatedContentModel = {
          ...contentModel,
          [domain]: {
            ...contentModel[domain],
            _data: {
              getAction: res // replace the existing domain data with the new data from the DXL response
            }
          }
        };
        // update the content model and expose the response on the saved data path

        // update the ULTA_SDK with the new updatedContentModel
        setContentModel( updatedContentModel );
      }

      return res;
    }
  };
};

/**
 * invokeModuleAction
 * @method
 *
 * @param { object } data - data configureation object
 * returns function
 */

export const invokeModuleAction = function( data, methods ){
  const { contentModel, targetModule, moduleName, domain, refetchRef, CURRENT_BREAKPOINT, user, isStaging, isNative, graphqlURI, graphqlDomain, graphqlServerUri } =
    data || {};
  const { setContentModel, DONOTUSE_broadcastInitAction } = methods;
  return async function( innerData ){
    const { actionPath, variables } = innerData || {};
    if( !targetModule._data ){
      // if there's no data we must first go fetch it then we can performe the invoke against it afterwards
      // TODO was looking into calling it for the hwoever the issue is that some arguments may be needed from an outside context, so we can't blindly call the getAction

      // eslint-disable-next-line no-console
      console.warn( `You must first run the 'get' action to get the module definition before invoking an action` );
      return;
    }

    // invokeAction( { actionPath: 'bonusOffers[0].buttonaction', variables: {}} )

    let requestedAction = _get( targetModule._data.getAction, actionPath );

    if( !requestedAction ){
      // eslint-disable-next-line no-console
      console.warn(
        `You tried to call the action ${actionPath} from the which is not defined on the current contentModel object`
      );
      return;
    }

    const actionResonse = await utils.triggerQuery( {
      action: requestedAction,
      contentModel,
      actionPath,
      refetchRef,
      CURRENT_BREAKPOINT,
      user,
      domain,
      isStaging,
      isNative,
      graphqlURI,
      graphqlDomain,
      graphqlServerUri
    }, { setContentModel, DONOTUSE_broadcastInitAction } )( variables );
    return actionResonse;
    // TODO call the setSDKACtion method with the module passed action
    // TODO (might be interseting t allow the user to only pass the action key and we retrieve the rest behind the scenes )
  };
};

/**
 * configureUIActionsa setups up dactions for the UI object in the SDK
 * @method
 * @param { object } data - data objects
 * @param { object } methods - injected methods
 * @returns object
 */
export const configureUIActions = function( data, methods ){
  const { ui = {}, flyoutContentRef } = data || {};
  const { openOverlay, closeOverlay, refetchRef, dispatchPortalUpdate, getFlyoutRef, broadcast } = methods || {};
  const enrichedUI = {
    closeOverlay,
    openOverlay: utils.handleOpenOverlay( { ui, flyoutContentRef }, { openOverlay, closeOverlay } ),
    refresh_page: utils.refreshPage( { refreshPageAction: ui.refreshPageAction }, { refetchRef } ),
    render: utils.renderModule( {}, { dispatchPortalUpdate } ),
    renderInOverlay: utils.renderInOverlay( { flyoutContentRef }, { openOverlay, dispatchPortalUpdate, closeOverlay, broadcast } )
  };
  return enrichedUI;
};

/**
 * @summary Maps over the content model and adds a `get` method for each entry.
 * @description This function iterates through the keys of the `contentModel` object and attaches a `get` method to each module.
 * This `get` method, when called, will fetch data for that module.
 * @method
 * @param {object} data - The data objects for configuration.
 * @param {object} data.contentModel The content model from DXL.
 * @param {string} data.CURRENT_BREAKPOINT The current device breakpoint.
 * @param {object} data.user The user data from context.
 * @param {boolean} data.isStaging Flag for staging environment.
 * @param {boolean} data.isNative Flag for native application environment.
 * @param {string} data.graphqlURI The URI for GraphQL requests.
 * @param {string} data.graphqlDomain The domain for the GraphQL server.
 * @param {string} data.graphqlServerUri The server URI for GraphQL.
 * @param {object} methods - Injected methods for the function to use.
 * @param {Function} methods.setContentModel The state setter to update the content model.
 * @returns {object} The enriched content model with `get` methods.
 */
export const configureGetMethods = function( data, methods ){
  const { setContentModel, DONOTUSE_broadcastInitAction } = methods || {};
  const { contentModel = {}, invoke, sdkAction, CURRENT_BREAKPOINT, user, isStaging, isNative, graphqlURI, graphqlDomain, graphqlServerUri } = data || {};
  //
  // let enrichedContentModel = { ...contentModel };
  Object.keys( contentModel )?.map( ( key ) => {
    const targetModule = contentModel[key];
    targetModule._data = targetModule._data || {}; // if there is data in the mdule return that otherwise instantiate with an empty object

    targetModule.get = utils.getModuleData(
      {
        contentModel,
        action: targetModule.getAction,
        actionPath: 'getAction',
        domain: key,
        _data: targetModule._data,
        CURRENT_BREAKPOINT,
        user,
        isStaging,
        isNative,
        graphqlURI,
        graphqlDomain,
        graphqlServerUri
      },
      { setContentModel, DONOTUSE_broadcastInitAction }
    );
  } );
  return { ...contentModel };
};

/**
 * @summary Maps over the content model and adds a `post` method for each entry.
 * @description This function iterates through the keys of the `contentModel` object and attaches a `post` method to each module.
 * This `post` method, when called, will execute a mutation for that module.
 * @method
 * @param {object} data - The data objects for configuration.
 * @param {object} data.contentModel The content model from DXL.
 * @param {string} data.CURRENT_BREAKPOINT The current device breakpoint.
 * @param {object} data.user The user data from context.
 * @param {boolean} data.isStaging Flag for staging environment.
 * @param {boolean} data.isNative Flag for native application environment.
 * @param {string} data.graphqlURI The URI for GraphQL requests.
 * @param {string} data.graphqlDomain The domain for the GraphQL server.
 * @param {string} data.graphqlServerUri The server URI for GraphQL.
 * @param {object} methods - Injected methods for the function to use.
 * @param {Function} methods.setContentModel The state setter to update the content model.
 * @returns {object} The enriched content model with `post` methods.
 */
export const configurePostMethods = function( data, methods ){
  const { setContentModel, DONOTUSE_broadcastInitAction } = methods || {};
  const { contentModel = {}, CURRENT_BREAKPOINT, user, isStaging, isNative, graphqlURI, graphqlDomain, graphqlServerUri } = data || {};

  Object.keys( contentModel )?.map( ( key ) => {
    const targetModule = contentModel[key];
    targetModule.post = utils.postModuleData(
      {
        contentModel,
        action: targetModule.postAction,
        actionPath: 'postAction',
        domain: key,
        _data: targetModule._data,
        CURRENT_BREAKPOINT,
        user,
        isStaging,
        isNative,
        graphqlURI,
        graphqlDomain,
        graphqlServerUri
      },
      { setContentModel, DONOTUSE_broadcastInitAction }
    );
  } );
  return { ...contentModel };
};

/**
 * UNRENDER_TYPES
 * @type {object}
 * @property {string} SINGLE='single' - The single render type
 * @property {string} ALL='all' - The all render type
 */

export const UNRENDER_TYPES = {
  SINGLE: 'single',
  ALL: 'all'
};

/**
 * distribueRenderMethods
 *
 * adds the render methods to the domains on the contentModel for eterna calls
 * @param { object } data - data objects
 * @param { object } methods - injected methods
 *
 * @returns object
 */
export const distributeRenderMethods = function( data, methods ){
  const { contentModel = {} } = data || {};
  const { dispatchPortalUpdate } = methods || {};

  let enrichedContentModel = { ...contentModel };
  Object.keys( contentModel )?.map( ( key ) => {
    const targetModule = enrichedContentModel[key];
    targetModule.render = utils.renderModule(
      { moduleName: targetModule.moduleName, domain: key },
      { dispatchPortalUpdate }
    );
    targetModule.unrender = utils.unRenderModule(
      { moduleName: targetModule.moduleName, domain: key, unrenderType: UNRENDER_TYPES.SINGLE },
      { dispatchPortalUpdate }
    );
    targetModule.unrenderAll = utils.unRenderModule(
      { moduleName: targetModule.moduleName, domain: key, unrenderType: UNRENDER_TYPES.ALL },
      { dispatchPortalUpdate }
    );
  } );
  return enrichedContentModel;
};

/**
 * getModuleData
 *
 * fetches data for a given contentModelObject and defeers to cache responses unless otherwise instructed
 * @method
 *
 * @param { object } data - data objects
 * @param { object } methods - injected methods
 *
 * @returns object
 */
export const getModuleData = function( data, methods ){
  const { contentModel, action, actionPath, domain, _data, CURRENT_BREAKPOINT, user } = data || {};
  const { setContentModel, DONOTUSE_broadcastInitAction } = methods || {};

  return async( customModuleParams, force ) => {
    // check the store first...if it's there reutrn it otherwise go to the outside world
    // TODO need to see if this should be throttled to every few minutes, so we don't unecessarily overload the serices with rogue calls
    if( _data[actionPath] && !force ){
      console.warn(`content for ${actionPath} fetched from the ULTA_SDK cache`, _data[actionPath]); //eslint-disable-line
      return _data[actionPath];
    }

    let clientParamsResolvedAction = getRequiredClientParams( {
      user,
      props: {},
      action,
      breakpoint: CURRENT_BREAKPOINT
    } );

    const preppedAction = queryProcessor( { action: clientParamsResolvedAction } );

    devLogger( {
      topic: LOG_TOPIC.Sdk,
      title: `[SDK] Requested for ${actionPath}`,
      value: { user, preppedAction }
    } );
    const { graphql, config, context, variables, method } = preppedAction;
    const dxlClient = getClient();
    // TODO result to be called using invoke
    const { data: dxlData } = await dxlClient.query( {
      query: gql`
        ${graphql}
      `,
      ...config,
      context: { ...context, fetchOptions: { method } },
      variables: {
        ...variables,
        moduleParams: {
          ...variables.moduleParams,
          ...customModuleParams
        }
      }
    } );

    if( dxlData?.Page?.meta?.dataCaptureEventList ){
      onResponseDataCapture( dxlData.Page.meta.dataCaptureEventList );
    }

    utils.broadcastSDKMeta( { meta: dxlData?.Page?.meta }, { DONOTUSE_broadcastInitAction } );

    const res = dxlData?.Page?.content;
    if( res ){
      let updatedContentModel = {
        ...contentModel,
        [domain]: {
          ...contentModel[domain],
          _data: {
            [actionPath]: res
          }
        }
      };
      setContentModel( updatedContentModel );
    }
    return res;
  };
};

/**
 * postModuleData
 *
 * posts data for a given contentModelObject
 * @method
 *
 * @param { object } data - data objects
 * @param { object } methods - injected methods
 *
 * @returns object
 */
export const postModuleData = function( data, methods ){
  const { contentModel, action, actionPath, domain, _data, CURRENT_BREAKPOINT, user } = data || {};
  const { setContentModel, DONOTUSE_broadcastInitAction } = methods || {};

  return async( customModuleParams ) => {
    if( !action ){
      // eslint-disable-next-line no-console
      console.warn( `postAction is not defined for module ${domain}` );
      return;
    }

    let clientParamsResolvedAction = getRequiredClientParams( {
      user,
      props: {},
      action,
      breakpoint: CURRENT_BREAKPOINT
    } );

    const preppedAction = queryProcessor( { action: clientParamsResolvedAction } );

    devLogger( {
      topic: LOG_TOPIC.Sdk,
      title: `[SDK] Posting for ${actionPath}`,
      value: { user, preppedAction }
    } );

    const { graphql, config, context, variables, method } = preppedAction;
    const dxlClient = getClient();

    const { data: dxlData } = await dxlClient.mutate( {
      mutation: gql`
        ${graphql}
      `,
      ...config,
      context: { ...context, fetchOptions: { method } },
      variables: {
        ...variables,
        moduleParams: {
          ...variables.moduleParams,
          ...customModuleParams
        }
      }
    } );

    if( dxlData?.Page?.meta?.dataCaptureEventList ){
      onResponseDataCapture( dxlData.Page.meta.dataCaptureEventList );
    }

    utils.broadcastSDKMeta( { meta: dxlData?.Page?.meta }, { DONOTUSE_broadcastInitAction } );

    const res = dxlData?.Page?.content;
    if( res ){
      let updatedContentModel = {
        ...contentModel,
        [domain]: {
          ...contentModel[domain],
          _data: {
            ..._data,
            getAction: res
          }
        }
      };
      setContentModel( updatedContentModel );
    }
    return res;
  };
};

/**
 * getProtalID
 * returns the ID for a rendered portal
 * @method
 *
 * @param { object } data - data objects
 *
 * @returns string
 */
export const getPortalID = function( { selector, moduleName, timeStamp = new Date() } ){
  return `${selector}_${moduleName}_${timeStamp.getTime()}`;
};

/**
 * unRenderModule
 * removes modules from the portals which are used to display them in the DOM
 * @method
 *
 * @param { object } data - data objects
 * @param { object } methods - injected methods
 *
 * @returns function
 */
export const unRenderModule = function( data, methods ){
  const { moduleName, domain, unrenderType } = data || {};
  const { dispatchPortalUpdate } = methods || {};
  return async( args ) => {
    const { id: portalID, moduleName: mName } = args || {};
    dispatchPortalUpdate( { type: utils.RENDER_TYPE.UNRENDER, unrenderType, portalID, moduleName: mName || moduleName } );
  };
};

/**
 * renderModule
 * adds modules to the portals variable for display in the browser
 * @method
 *
 * @param { object } data - data objects
 * @param { object } methods - injected methods
 *
 * @returns function
 */
export const renderModule = function( data, methods ){
  const { moduleName, domain = 'ui' } = data || {};
  const { dispatchPortalUpdate } = methods || {};
  return function( args ){

    const { selector, data = {}, componentName, timeStamp = new Date() } = args || {};

    if( !selector ){
      console.warn('A DOM selector must be passed in order to render a module'); //eslint-disable-line
      return;
    }
    const portalID = utils.getPortalID( { selector, moduleName: args.data.moduleName, timeStamp } );

    dispatchPortalUpdate( {
      type: utils.RENDER_TYPE.RENDER,
      selector,
      moduleName: componentName || args.data.moduleName,
      data,
      portalID,
      domain
    } );

    return portalID;
  };
};

/**
 * renderInOverlay - Combines overlay opening with module rendering via portal system
 * @method
 * @param { object } outerData - outer data configuration
 * @param { object } methods - injected methods
 * @returns { function } - renderInOverlay function
 */
export const renderInOverlay = ( outerData, methods ) => {
  const { flyoutContentRef } = outerData || {};
  const { openOverlay, dispatchPortalUpdate, closeOverlay, broadcast } = methods || {};
  return async function( config ){
    const {
      moduleName,
      contentId,
      overlayType = 'flyout',
      props = {},
      headerText = moduleName,
      alignment = 'right',
      showDynamicFlyoutHeader = true,
      crossButtonVisibility = true,
      confirmOnExit = {},
      mobileOffsetElement = '.PrimaryBar',
      customOnClose,
      customCloseEvent
    } = config || {};

    if( !moduleName ){
      // eslint-disable-next-line no-console
      console.error( 'renderInOverlay: moduleName is required' );
      return;
    }

    // Create unique container ID for portal
    const containerId = `sdk_overlay_${Date.now()}`;
    const selector = `#${containerId}`;

    // Mobile detection and offset element
    const isMobile = !isServer() && window.innerWidth <= 768;
    const offsetElement = isMobile && mobileOffsetElement ?
      { current: document.querySelector( mobileOffsetElement ) } : null;

    // Track overlay state
    const overlayState = { isOpen: false };

    // Create cleanup function
    const cleanup = () => {
      overlayState.isOpen = false;

      // Call custom close handler if provided
      if( customOnClose ){
        customOnClose();
      }

      /**
       * Manually closes the OverlayFlyout by removing the `data-open` attribute and resetting styles.
       * This is necessary for consistent cleanup behavior across mobile browsers, as some
       * mobile environments (like iOS Safari or Android Chrome) can exhibit unreliable behavior with overlays:
       *
       * - Focus/blur events may not reliably trigger on touch devices
       * - Overlay stacking and context can become inconsistent during navigation or virtual keyboard display
       * - Touch event propagation may bypass traditional click/focus logic
       * - CSS transitions and state attributes may not reflect accurately during rapid UI interactions
       *
       * Therefore, we explicitly remove the overlay state and listen for `transitionend` to ensure the
       * portal is unrendered correctly and synchronously cleaned up from the DOM.
       */
      const overlayFlyout = document.querySelector( '.OverlayFlyout[data-open]' );
      if( overlayFlyout ){
        overlayFlyout.removeAttribute( 'data-open' );
        overlayFlyout.style.pointerEvents = 'none';
        overlayFlyout.style.top = '';

        overlayFlyout.addEventListener( 'transitionend', () => {
          dispatchPortalUpdate( {
            type: utils.RENDER_TYPE.UNRENDER,
            portalID: containerId,
            moduleName,
            unrenderType: utils.UNRENDER_TYPES.SINGLE
          } );
        }, { once: true } );
      }
      else {
        // Cleanup portal
        dispatchPortalUpdate( {
          type: utils.RENDER_TYPE.UNRENDER,
          portalID: containerId,
          moduleName,
          unrenderType: utils.UNRENDER_TYPES.SINGLE
        } );
      }
    };

    // Handle close with confirmation if needed
    const handleClose = () => {
      if( confirmOnExit.enabled && overlayState.isOpen ){
        const message = confirmOnExit.message || 'Are you sure you want to close?';
        if( !window.confirm( message ) ){
          return; // Don't close if user cancels
        }
      }

      if( customCloseEvent ){
        broadcast( { id: customCloseEvent } );
      }
      else {
        cleanup();
      }
    };
    // Enhanced props with overlay control
    const enhancedProps = {
      ...props,
      onOverlayClose: () => {
        closeOverlay();
        cleanup();
      }
    };

    // Step 1: Open overlay with empty container
    openOverlay( {
      type: overlayType,
      headerText,
      showDynamicFlyoutHeader,
      crossButtonVisibility,
      customCloseEvent,
      alignment,
      ...( offsetElement && { offsetElement } ),
      content: (
        <div
          id={ containerId }
          ref={ flyoutContentRef }
          style={ { height: '100%' } } // Ensure container is visible
        />
      ),
      onClose: handleClose
    } );

    overlayState.isOpen = true;

    // Step 2: Wait for DOM to be ready
    const waitForElement = async( selector, maxTries = 10, delay = 100 ) => {
      for ( let i = 0; i < maxTries; i++ ){
        const el = document.querySelector( selector );
        if( el ){
          return el;
        }
        await new Promise( res => setTimeout( res, delay ) );
      }
      return null;
    };

    const containerElement = await waitForElement( selector );
    if( !containerElement ){
      // eslint-disable-next-line no-console
      console.error( `renderInOverlay: Container ${selector} not found in DOM` );
      closeOverlay();
      return;
    }

    // Step 3: Render module via portal system with all required fields
    try {
      dispatchPortalUpdate( {
        type: utils.RENDER_TYPE.RENDER,
        selector,
        moduleName,
        data: {
          moduleName,
          id: contentId || `${moduleName}_${Date.now()}`,
          containerLayerHostContentId: contentId,
          ...enhancedProps
        },
        domain: 'ui',
        portalID: containerId
      } );

      devLogger( {
        topic: LOG_TOPIC.Sdk,
        title: `[SDK] renderInOverlay: Rendered ${moduleName}`,
        value: { containerId, selector, props: enhancedProps }
      } );
    }
    catch ( error ){
      // eslint-disable-next-line no-console
      console.error( `renderInOverlay: Failed to render ${moduleName}:`, error );
      closeOverlay();
      return;
    }

    // Return handle for external control
    return {
      portalId: containerId,
      containerId,
      selector,
      close: () => {
        closeOverlay();
        cleanup();
      },
      updateProps: ( newProps ) => {
        const updatedProps = { ...enhancedProps, ...newProps };
        dispatchPortalUpdate( {
          type: 'UPDATE',
          portalID: containerId,
          data: updatedProps
        } );
      },
      isOpen: () => overlayState.isOpen
    };
  };
};

export default SDK;
